import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  billingCurrencies,
  supportedLocales,
  updateClubDataRequestSchema,
  type ClubDataView,
  type SupportedLocale,
} from '@stm/contracts';
import { translate } from '@stm/i18n';
import { ApiRequestError, apiRequest } from './api';

export interface ClubDataPanelProps {
  locale: SupportedLocale;
  organizationId: string;
  csrfToken: string;
  /** Reloads the signed-in account so the header shows a renamed club. */
  refreshAccount: () => Promise<void>;
}

type ErrorKey = ApiRequestError['messageKey'];

interface ClubDataForm {
  name: string;
  defaultLocale: string;
  timezone: string;
  currency: string;
}

function formOf(data: ClubDataView): ClubDataForm {
  return {
    name: data.name,
    defaultLocale: data.defaultLocale,
    timezone: data.timezone,
    currency: data.currency,
  };
}

/** Every time zone the browser knows, with the club's current one kept even if it is not listed. */
function timeZoneOptions(current: string): string[] {
  const known = Intl.supportedValuesOf('timeZone');
  return current === '' || known.includes(current) ? known : [current, ...known];
}

/** The club's name, language, time zone and currency. */
export function ClubDataPanel({
  locale,
  organizationId,
  csrfToken,
  refreshAccount,
}: ClubDataPanelProps): React.JSX.Element {
  const [data, setData] = useState<ClubDataView | null>(null);
  const [form, setForm] = useState<ClubDataForm | null>(null);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [saved, setSaved] = useState(false);
  const path = `/api/v1/organizations/${organizationId}/club-data`;

  const load = useCallback(async () => {
    try {
      const loaded = await apiRequest<ClubDataView>(path);
      setData(loaded);
      setForm(formOf(loaded));
    } catch (error) {
      setErrorKey(error instanceof ApiRequestError ? error.messageKey : 'error.unexpected');
    }
  }, [path]);

  useEffect(() => {
    void load();
  }, [load]);

  const timeZones = useMemo(() => timeZoneOptions(data?.timezone ?? ''), [data?.timezone]);
  const request = updateClubDataRequestSchema.safeParse(form);
  const nameValid = form !== null && form.name.trim() !== '';

  function change(field: keyof ClubDataForm, value: string): void {
    setSaved(false);
    setForm((current) => (current === null ? current : { ...current, [field]: value }));
  }

  async function save(): Promise<void> {
    if (data === null || !request.success) return;
    setErrorKey(null);
    setSaved(false);
    try {
      const updated = await apiRequest<ClubDataView>(path, {
        method: 'PUT',
        body: request.data,
        csrfToken,
        ifMatchVersion: data.version,
      });
      setData(updated);
      setForm(formOf(updated));
      setSaved(true);
      if (updated.name !== data.name) await refreshAccount();
    } catch (error) {
      setErrorKey(error instanceof ApiRequestError ? error.messageKey : 'error.unexpected');
    }
  }

  return (
    <Box component="section" aria-labelledby="club-data-title" sx={{ mb: 3 }}>
      <Typography variant="h6" component="h2" id="club-data-title" gutterBottom>
        {translate(locale, 'clubData.title')}
      </Typography>
      <Typography color="text.secondary" gutterBottom>
        {translate(locale, 'clubData.note')}
      </Typography>
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {saved && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {translate(locale, 'clubData.saved')}
        </Alert>
      )}
      {form === null ? (
        errorKey === null && <Typography>{translate(locale, 'clubData.loading')}</Typography>
      ) : (
        <Stack
          component="form"
          spacing={2}
          sx={{ maxWidth: 480 }}
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <TextField
            required
            size="small"
            label={translate(locale, 'clubData.name')}
            value={form.name}
            error={!nameValid}
            helperText={nameValid ? ' ' : translate(locale, 'clubData.nameRequired')}
            onChange={(event) => {
              change('name', event.target.value);
            }}
            slotProps={{ htmlInput: { maxLength: 200 } }}
          />
          <TextField
            select
            size="small"
            label={translate(locale, 'clubData.language')}
            helperText={translate(locale, 'clubData.languageHelp')}
            value={form.defaultLocale}
            onChange={(event) => {
              change('defaultLocale', event.target.value);
            }}
          >
            {supportedLocales.map((option) => (
              <MenuItem key={option} value={option}>
                {translate(locale, `locale.${option}`)}
              </MenuItem>
            ))}
          </TextField>
          <Autocomplete
            size="small"
            disableClearable
            options={timeZones}
            value={form.timezone}
            onChange={(_event, value) => {
              change('timezone', value);
            }}
            renderInput={(parameters) => (
              <TextField
                {...parameters}
                label={translate(locale, 'clubData.timezone')}
                helperText={translate(locale, 'clubData.timezoneHelp')}
              />
            )}
          />
          <TextField
            select
            size="small"
            label={translate(locale, 'clubData.currency')}
            helperText={translate(locale, 'clubData.currencyHelp')}
            value={form.currency}
            onChange={(event) => {
              change('currency', event.target.value);
            }}
          >
            {billingCurrencies.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
          <Box>
            <Button type="submit" variant="contained" disabled={!request.success}>
              {translate(locale, 'clubData.save')}
            </Button>
          </Box>
        </Stack>
      )}
    </Box>
  );
}
